import { A11yModule } from '@angular/cdk/a11y';
import { BidiModule } from '@angular/cdk/bidi';
import { ObserversModule } from '@angular/cdk/observers';
import { OverlayModule } from '@angular/cdk/overlay';
import { PlatformModule } from '@angular/cdk/platform';
import { PortalModule } from '@angular/cdk/portal';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CdkStepperModule } from '@angular/cdk/stepper';
import { CdkTableModule } from '@angular/cdk/table';
import { registerLocaleData } from '@angular/common';
import localeAR from '@angular/common/locales/ar';
import localeDE from '@angular/common/locales/de';
import localeEN from '@angular/common/locales/en';
import localeFR from '@angular/common/locales/fr';
import { forwardRef, LOCALE_ID, ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
   MatAutocompleteModule,
   MatButtonModule,
   MatButtonToggleModule,
   MatCardModule,
   MatCheckboxModule,
   MatChipsModule,
   MatDatepickerModule,
   MatDialogModule,
   MatExpansionModule,
   MatGridListModule,
   MatIconModule,
   MatInputModule,
   MatListModule,
   MatMenuModule,
   MatNativeDateModule,
   MatPaginatorIntl,
   MatPaginatorModule,
   MatProgressBarModule,
   MatProgressSpinnerModule,
   MatRadioModule,
   MatRippleModule,
   MatSelectModule,
   MatSidenavModule,
   MatSliderModule,
   MatSlideToggleModule,
   MatSnackBarModule,
   MatSortModule,
   MatStepperModule,
   MatTableModule,
   MatTabsModule,
   MatToolbarModule,
   MatTooltipModule,
} from '@angular/material';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { ApiEvents, ApiModule, FallbackProvider, TranslateService } from 'vas-mobile-api';

import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { routing } from './app.routing';
import { DashboardComponent } from './dashboard/dashboard.component';
import { DeliveriesListComponent } from './deliveries-list/deliveries-list.component';
import { getTranslationsPaginatorIntl } from './deliveries-list/translation-paginator-intl';
import { DeliveryDetailComponent } from './delivery-detail/delivery-detail.component';
import { InfiniteScrollerDirective } from './directives/infinite-scroller.directive';
import { DateMobileComponent } from './formular-generator/forms-mobile/dateMobile/dateMobile.component';
import { SelectMobileComponent } from './formular-generator/forms-mobile/selectMobile/selectMobile.component';
import { CheckboxStandardComponent } from './formular-generator/forms-standard/checkboxStandard/checkboxStandard.component';
import { DateStandardComponent } from './formular-generator/forms-standard/dateStandard/dateStandard.component';
import { InputStandardComponent } from './formular-generator/forms-standard/inputStandard/inputStandard.component';
import { RadioStandardComponent } from './formular-generator/forms-standard/radioStandard/radioStandard.component';
import { SelectStandardComponent } from './formular-generator/forms-standard/selectStandard/selectStandard.component';
import { LoadingSlotPopupComponent } from './loading-slot-popup/loading-slot-popup.component';
import { LoadingSlotVisualComponent } from './loading-slot-visual/loading-slot-visual.component';
import { LoginComponent } from './login/login.component';
import { NewDeliveryComponent } from './new-delivery/new-delivery.component';
import { NewInboundDeliveryComponent } from './new-inbound-delivery/new-inbound-delivery.component';
import { PasswordResetComponent } from './password-reset/password-reset.component';
import { PdfViewerComponent } from './pdf-viewer/pdf-viewer.component';
import { PictureViewerComponent } from './picture-viewer/picture-viewer.component';
import { ReportDetailComponent } from './report-detail/report-detail.component';
import { ReportsListComponent } from './reports-list/reports-list.component';
import { SnakeComponent } from './secret/snake.component';
import { AuthguardService } from './services/authguard.service';
import { DataExchangeService } from './services/dataexchange.service';
import { DeliveryBarrierService } from './services/delivery-barrier.service';
import { SecretService } from './services/secret.service';
import { TranslateFallbackProvider } from './services/translate-fallback-provider';
import { SetPasswordComponent } from './set-password/set-password.component';
import { SettingsComponent } from './settings/settings.component';
import { StatusListComponent } from './status-list/status-list.component';
import { TransactionDetailComponent } from './transaction-detail/transaction-detail.component';
import { TransactionsListComponent } from './transactions-list/transactions-list.component';

registerLocaleData(localeDE, 'DE');
registerLocaleData(localeFR, 'FR');
registerLocaleData(localeEN, 'EN');
registerLocaleData(localeAR, 'AR');

const providers = [
  { provide: LOCALE_ID, useValue: 'de-de' }, AuthguardService, DataExchangeService, SecretService, DeliveryBarrierService];
// include this for mockup data
// , { provide: HTTP_INTERCEPTORS, useClass: MockupInterceptor, multi: true }];

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    SettingsComponent,
    DashboardComponent,
    DeliveryDetailComponent,
    LoadingSlotVisualComponent,
    ReportsListComponent,
    ReportDetailComponent,
    NewDeliveryComponent,
    StatusListComponent,
    DeliveriesListComponent,
    InfiniteScrollerDirective,
    PictureViewerComponent,
    SnakeComponent,
    SetPasswordComponent,
    TransactionsListComponent,
    TransactionDetailComponent,
    PdfViewerComponent,
    DateMobileComponent,
    SelectMobileComponent,
    CheckboxStandardComponent,
    DateStandardComponent,
    InputStandardComponent,
    RadioStandardComponent,
    SelectStandardComponent,
    PasswordResetComponent,
    LoadingSlotPopupComponent,
    NewInboundDeliveryComponent
  ],

  imports: [
    MatPaginatorModule,
    MatTableModule,
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    forwardRef(() => MaterialModule),
    ReactiveFormsModule,
    CalendarModule.forRoot({
      provide: DateAdapter,
      useFactory: adapterFactory
    }),
    ApiModule.forRoot({
      environment,
      configPath: 'assets/config/config.json',
      isOfflineSupported: false,
      fallbackProvider: { provide: FallbackProvider, useClass: TranslateFallbackProvider },
      reference: 'YCC_idispo',
      toastConfig: { preventDuplicates: true }
    }),
    routing
  ],
  exports: [
    forwardRef(() => MaterialModule),
    ReactiveFormsModule,
    ApiModule
  ],
  providers: [providers, { provide: MatPaginatorIntl, useFactory: translationPaginatorIntlFactory, deps: [TranslateService, ApiEvents] }],
  bootstrap: [AppComponent],
  entryComponents: [PictureViewerComponent, PdfViewerComponent, PasswordResetComponent, LoadingSlotPopupComponent]
})
export class AppModule { }

@NgModule({})
export class BaseSharedModule {
  static forRoot(): ModuleWithProviders {
    return {
      ngModule: AppModule,
      providers: [providers]
    };
  }
}

export function translationPaginatorIntlFactory(translateService: TranslateService, apiEvents: ApiEvents) {
  return getTranslationsPaginatorIntl(translateService, apiEvents);
}

/**
 * NgModule that includes all Material modules that are required to serve
 * the Plunker.
 */
@NgModule({
  exports: [
    // CDK
    A11yModule,
    BidiModule,
    ObserversModule,
    OverlayModule,
    PlatformModule,
    PortalModule,
    ScrollingModule,
    CdkStepperModule,
    CdkTableModule,

    // Material
    MatAutocompleteModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDatepickerModule,
    MatDialogModule,
    MatExpansionModule,
    MatGridListModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatMenuModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatRippleModule,
    MatSelectModule,
    MatSidenavModule,
    MatSlideToggleModule,
    MatSliderModule,
    MatSnackBarModule,
    MatStepperModule,
    MatTableModule,
    MatTabsModule,
    MatToolbarModule,
    MatTooltipModule,
    MatNativeDateModule,
    MatSortModule
  ]
})
export class MaterialModule {
}
